import { Rating } from '@/types';
import { mapProfessionalLicenseRows } from '@/utils/professional/professional-license.util';
import {
  FacilityType,
  ProcedureTypeEnum,
  ScheduleSearch,
  ScheduleType,
  SchedulesSearchPartnerFacility,
  SchedulesSearchProcedure,
  SchedulesSearchProfessional,
  SearchPartnerData,
  SearchType,
} from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import {
  SearchApiFacility,
  SearchApiPartner,
  SearchApiProcedure,
  SearchApiProcedurePricing,
  SearchApiProcedureType,
  SearchApiProfessional,
  SearchApiRating,
  SearchApiSchedule,
  SearchApiScheduleGroup,
} from '@/app/(main)/(pages)/_types/search-api-response.type';

export interface MapToSearchPartnerDataArgs {
  group: SearchApiScheduleGroup;
  procedures: SearchApiProcedure[];
  professional: SearchApiProfessional | null;
  partner: SearchApiPartner | null;
  facility: SearchApiFacility | null;
  rating: SearchApiRating | null;
  pricing: SearchApiProcedurePricing | null;
  searchType?: SearchType;
}

function mapRating(rating: SearchApiRating | null): Rating {
  return {
    commentsCount: rating?.commentsCount ?? 0,
    fiveStars: {
      average: rating?.average ?? 0,
      count: rating?.ratingsCount ?? 0,
      tiers: rating?.tiers,
    },
  };
}

function mapFacility(
  facility: SearchApiFacility | null,
  fallbackId: string,
): SchedulesSearchPartnerFacility {
  return {
    IBGECode: facility?.ibgeCode ?? '',
    id: facility?.id ?? fallbackId,
    addressLine1: facility?.addressLine1 ?? '',
    addressLine2: facility?.addressLine2 ?? undefined,
    city: facility?.city ?? '',
    contacts: {
      email: facility?.contacts?.email ?? '',
      phones: facility?.contacts?.phones ?? [],
      whatsapp: facility?.contacts?.whatsapp ?? '',
    },
    name: facility?.name ?? '',
    neighborhood: facility?.neighborhood ?? '',
    number: facility?.number ?? '',
    rating: undefined,
    state: facility?.state ?? '',
    type: facility?.type === 'MAIN' ? FacilityType.MAIN : FacilityType.BRANCH,
    zipcode: facility?.zipcode ?? '',
    slug: facility?.slug ?? undefined,
  };
}

function mapProcedures(
  group: SearchApiScheduleGroup,
  procedures: SearchApiProcedure[],
  pricing: SearchApiProcedurePricing | null,
  searchTypeOverride?: SearchType,
): SchedulesSearchProcedure[] {
  const offeredIds = new Set(group.procedures.map((groupProcedure) => groupProcedure.id));
  const isLaboratory = procedures[0]?.type === SearchApiProcedureType.LABORATORY;
  const searchType = searchTypeOverride
    ?? (isLaboratory ? SearchType.LABORATORIAL_EXAM : SearchType.PRESENTIAL_CONSULTATION);

  return procedures.map((procedure) => {
    const isOffered = offeredIds.has(procedure.id);
    const priced = pricing?.procedures.find((item) => item.procedureId === procedure.id);
    const promotion = priced?.promotion ?? null;

    return {
      id: procedure.id,
      name: procedure.name,
      code: procedure.code ?? '',
      type: isLaboratory ? ProcedureTypeEnum.LABORATORY : ProcedureTypeEnum.NON_LABORATORY,
      tussCode: procedure.tussCode ?? '',
      description: procedure.description ?? '',
      instructions: '',
      originalPrice: promotion?.originalPrice,
      price: isOffered ? (priced?.price ?? 0) : undefined,
      selected: false,
      availableForTelehealth: procedure.availableForTelehealth ?? false,
      searchType,
      slug: procedure.slug,
      isAvailable: isOffered,
      promotionId: promotion?.id,
    };
  });
}

function buildScheduleList(
  schedules: SearchApiSchedule[] | undefined,
  type: ScheduleType,
): ScheduleSearch[] {
  return (schedules ?? []).map((schedule) => ({
    id: schedule.id,
    type,
    slots: [],
    workingPeriods: (schedule.workingPeriods ?? []).map((period) => ({
      id: period.id,
      start: period.start,
      end: period.end,
      weekday: period.weekday,
      duration: period.duration,
    })),
    notes: undefined,
    waitingTime: schedule.waitingTime != null ? String(schedule.waitingTime) : undefined,
    name: undefined,
    minAge: schedule.minAge ?? 0,
    maxAge: schedule.maxAge ?? 0,
    followUp: undefined,
  }));
}

export function mapToSearchPartnerData({
  group,
  procedures,
  professional,
  partner,
  facility,
  rating,
  pricing,
  searchType,
}: MapToSearchPartnerDataArgs): SearchPartnerData {
  const facilityMapped = mapFacility(facility, group.partnerFacilityId ?? '');

  const professionalMapped: SchedulesSearchProfessional = {
    avatar: professional?.avatar ?? '',
    honorific: professional?.honorific ?? '',
    gender: professional?.gender ?? '',
    id: group.professionalId ?? '',
    name: professional?.name ?? '',
    slug: professional?.slug ?? '',
    rating: mapRating(rating),
    licenses: mapProfessionalLicenseRows(professional),
    createdAt: '',
  };

  const schedulesByType = group.schedulesByScheduleType;

  return {
    distance: group.distance ?? undefined,
    partner: {
      avatar: partner?.avatar ?? '',
      distance: group.distance != null ? String(group.distance) : '',
      description: partner?.description ?? '',
      facility: facilityMapped,
      id: group.partnerId ?? '',
      score: group.score,
      socialName: partner?.socialName ?? '',
    },
    priceScore: '',
    procedures: mapProcedures(group, procedures, pricing, searchType),
    description: '',
    proceduresFoundScore: 0,
    professional: professionalMapped,
    schedulesTypes: {
      [ScheduleType.SCHEDULED]: buildScheduleList(schedulesByType?.scheduled, ScheduleType.SCHEDULED),
      [ScheduleType.ARRIVAL_ORDER]: buildScheduleList(schedulesByType?.arrivalOrder, ScheduleType.ARRIVAL_ORDER),
      [ScheduleType.FLEX]: buildScheduleList(schedulesByType?.flex, ScheduleType.FLEX),
      [ScheduleType.LABORATORIAL]: buildScheduleList(schedulesByType?.laboratorial, ScheduleType.LABORATORIAL),
    },
    score: group.score,
    timeOffset: String(group.timeOffset),
    timeZone: group.timeZone,
  };
}
